import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chatAPI, type ChatRequest, type ChatMessage } from '../services/api/chat.service';

// Query keys
export const chatKeys = {
  all: ["chat"] as const,
  conversations: (startupId: string) => [...chatKeys.all, "conversations", startupId] as const,
};

// Hook for sending chat messages
export function useSendMessage(startupId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (request: ChatRequest) => chatAPI.sendMessage(startupId, request),
    onSuccess: () => {
      // Optionally invalidate conversations to refresh chat history
      queryClient.invalidateQueries({ queryKey: chatKeys.conversations(startupId) });
    },
  });
}

// Hook for getting conversation history
export function useConversations(startupId: string) {
  return useQuery({
    queryKey: chatKeys.conversations(startupId),
    queryFn: () => chatAPI.getConversations(startupId),
    enabled: !!startupId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Custom hook for managing chat state
export function useChatState() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  
  const addMessage = (message: ChatMessage) => {
    setMessages((prev: ChatMessage[]) => [...prev, message]);
  };
  
  const clearMessages = () => {
    setMessages([]);
  };
  
  return {
    messages,
    isOpen,
    setIsOpen,
    addMessage,
    clearMessages,
  };
}
